import { isEmpty } from 'radash';
import { useEffect, useRef } from 'react';

import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

/**
 * Owns the single ChannelService registration for ITEM_EDITED while the item
 * editor is open. Field-sync and ConnectedUsers must only listen/broadcast —
 * not call register again — otherwise DevTools shows multiple
 * `itemEdited?identifier=…` realtime calls for the same item.
 *
 * Mirrors the legacy Angular editor which registered once with listen=true
 * when the item was editable. ChannelService itself also refcounts so remounts
 * do not re-hit the network.
 */
const useItemEditedChannel = ({
    primaryKey,
    listen = true,
}: {
    primaryKey?: string;
    listen?: boolean;
}) => {
    const { subscribe, unsubscribe } = useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.ITEM_EDITED_EVENT,
        identifier: primaryKey,
    });

    // Keep latest subscribe/unsubscribe without re-running the effect when
    // useCallback identities change (would otherwise unregister+register again).
    const subscribeRef = useRef(subscribe);
    const unsubscribeRef = useRef(unsubscribe);
    subscribeRef.current = subscribe;
    unsubscribeRef.current = unsubscribe;

    useEffect(() => {
        if (!primaryKey || isEmpty(primaryKey)) {
            return;
        }

        subscribeRef.current(listen);

        return () => {
            unsubscribeRef.current(listen);
        };
    }, [primaryKey, listen]);
};

export { useItemEditedChannel };
